import { CheckpointTask } from '@/graphql/generated/schemas'

import { CheckpointTaskInputI } from '../types/types'

/**
 * Returns a formatted checklist items
 * Removes whitespaces and if an item contains an id with a 'temp-' value removes its id property
 *
 * @param items - The checklist items to be formatted
 * @returns The formatted checklist items, trimmed and temp id removed
 */
export const formatItemsForParent = (
  items: { value: string; id: string }[]
) => {
  return items
    .filter((item) => item.value.trim() !== '')
    .map((item) => ({
      ...(item.id && !item.id.startsWith('temp-') ? { id: item.id } : {}),
      value: item.value,
    }))
}

/**
 * Checks to see if the checklist items have been updated.
 *
 * @param formattedItems - The formatted checklist items
 * @param checkpointTasks - The orginal checkpointTask items returned from the API
 * @returns A boolean value true/false based off the if the two arrays are equal
 */
export const hasChecklistUpdated = (
  formattedItems: CheckpointTaskInputI[],
  checkpointTasks: CheckpointTask[]
): boolean => {
  const originalFormattedItems =
    (checkpointTasks || [])
      .filter((task) => task.description?.trim())
      .map((task) => ({
        id: task.id,
        value: task.description,
      }))
      ?.sort((a, b) => a?.value.localeCompare(b?.value)) || []

  return (
    JSON.stringify(formattedItems) !== JSON.stringify(originalFormattedItems)
  )
}

/**
 * Maps the checkpointTasks as checklist items with an id and value
 *
 * @param checkpointTasks - The checkpointTasks
 * @returns A sorted array of checklist items with an id and value
 */
export const getChecklistItems = (checkpointTasks: CheckpointTask[]) =>
  checkpointTasks
    ?.map((task) => ({
      id: task?.id || '',
      value: task?.description || '',
    }))
    ?.sort((a, b) => a?.value.localeCompare(b?.value)) || []

/**
 * Checks if a given checklist item description has been updated
 *
 * @param task -
 * @param checkpointTasks -
 * @returns A boolean value true/false based off if the given task description has been updated from the orginal task description
 */ export const hasDescriptionUpdated = (
  task: CheckpointTaskInputI,
  checkpointTasks: CheckpointTask[]
) => {
  const originalDescription = checkpointTasks?.find(
    (orginalTask) => orginalTask.id === task.id
  )?.description
  return task.value !== originalDescription
}

/**
 * Checks if a checklist contains items with the same name
 *
 * @param options -
 * @returns A boolean value true/false based off if a name occuries more than once
 */
export const hasDuplicateChecklistItems = (
  checklist: CheckpointTaskInputI[]
) => {
  const itemNames = checklist.map((el) => el.value)
  return itemNames.length !== new Set(itemNames).size
}
